import { ILLEGAL_NAME_CHARS } from './exportNaming';
import type { ExportItem, ExportMediaType, ExportSource } from './types';

const isHttp = (v: unknown): v is string => typeof v === 'string' && /^https?:\/\//i.test(v);

function mediaTypeOf(raw: unknown): ExportMediaType {
  const value = String(raw ?? '').toLowerCase();
  if (!value || value === 'photo' || value.startsWith('image')) return 'photo';
  if (value.startsWith('video')) return 'video';
  return 'other';
}

const NOT_A_PREVIEW = /\.(heic|heif|mp4|mov|m4v|webm|avi)$/i;
const isInline = (v: unknown): v is string => typeof v === 'string' && v.startsWith('data:');

/** A small image to show for the asset: never the original itself, a video, or a HEIC. */
function previewOf(a: Record<string, any>, originalUrl: string | null): string | null {
  const inline = [a.thumbnail_uri, a.thumbnail].find(isInline);
  if (inline) return inline;
  const usable = (v: unknown): v is string =>
    isHttp(v) && v !== originalUrl && v !== a.web_video_url && !NOT_A_PREVIEW.test(v.split(/[?#]/)[0]);
  return [a.thumbnail_uri, a.thumbnail, a.web_uri].find(usable) ?? null;
}

/** Assets from admin-albums-api and public-album-view. */
export function fromAlbumAsset(a: Record<string, any>): ExportSource {
  const originalUrl = isHttp(a.path) ? a.path : isHttp(a.asset_uri) ? a.asset_uri : null;
  return {
    id: String(a.asset_id || a.id),
    originalUrl,
    thumbnailUrl: previewOf(a, originalUrl),
    mediaType: mediaTypeOf(a.asset_type ?? a.media_type),
    createdAt: a.created_at ?? null,
  };
}

/** The storage basename of a URL, made safe as a filename; the asset id if there is none. */
export function filenameFromUrl(url: string, id: string): string {
  let name = '';
  try {
    name = new URL(url).pathname.split('/').pop() ?? '';
    name = decodeURIComponent(name);
  } catch {
    // Not a URL: name stays empty. A stray "%" (100%.jpg): keep the undecoded segment.
  }
  const cleaned = name.replace(ILLEGAL_NAME_CHARS, '_').trim();
  return /^[. ]*$/.test(cleaned) ? id : cleaned;
}

/** Builds the export list. Names are unique ignoring case, so no file overwrites another. */
export function buildExportItems(sources: ExportSource[]): { items: ExportItem[]; unavailable: number } {
  const seenIds = new Set<string>();
  const seenNames = new Set<string>();
  const items: ExportItem[] = [];
  let unavailable = 0;

  for (const source of sources) {
    if (seenIds.has(source.id)) continue;
    seenIds.add(source.id);
    if (!source.originalUrl) {
      unavailable += 1;
      continue;
    }
    let filename = filenameFromUrl(source.originalUrl, source.id);
    if (seenNames.has(filename.toLowerCase())) filename = `${source.id}_${filename}`;
    seenNames.add(filename.toLowerCase());
    items.push({
      id: source.id,
      url: source.originalUrl,
      filename,
      thumbnailUrl: source.thumbnailUrl,
      mediaType: source.mediaType,
      createdAt: source.createdAt,
      size: null,
    });
  }
  return { items, unavailable };
}
